<template>
  <div class="bj-col bj-col-12">
    <!-- 原页面：只展示信息 + 修改按钮 -->
    <div class="bj-form-title">
      {{ $t('ExternalOrders.newAdd.receivingInformation') }}
      <span v-if="isShowEdit" class="link right" @click="openDialog">{{ $t('InternalSalesOrderDetails.Modify') }}</span>
    </div>
    <div v-for="field in displayFields" :key="field.key" class="bj-form-item">
      <div class="bj-formItemLabel">{{ $t(field.label) }}</div>
      <div class="bj-form-item-value">{{ receiverInfoVO && receiverInfoVO[field.key] }}</div>
    </div>

    <!-- 弹出框 -->
    <el-dialog
      :visible.sync="dialogVisible"
      width="870px"
      :close-on-click-modal="false"
      append-to-body
      class="buyer-info-dialog"
    >
      <span slot="title" class="dialog-custom-title">
        <i class="el-icon-location-outline" style="margin-right:6px;font-size:18px;vertical-align:middle;color:#409EFF;"></i>
        <span style="vertical-align:middle;">{{ $t('ExternalOrders.newAdd.chooseShippingAddress') }}</span>
      </span>
      <!-- 警告提示：存在字段差异或验证失败时显示 -->
      <div v-if="hasAnomaly || hasInvalidFields" class="anomaly-alert">
        <i class="el-icon-warning" style="color:#f56c6c;margin-right:6px;"></i>
        <template v-if="hasInvalidFields">
          {{ $t('ExternalOrders.newAdd.anomalyDetected3Pre') }}
          <a href="https://www.google.com/maps" target="_blank" class="anomaly-maps-link">Google Maps</a>
          {{ $t('ExternalOrders.newAdd.anomalyDetected3Post') }}
        </template>
        <template v-else>
          {{ $t(hasValidSuggestion ? 'ExternalOrders.newAdd.anomalyDetected' : 'ExternalOrders.newAdd.anomalyDetected2') }}
        </template>
      </div>

      <!-- 两列内容 -->
      <div class="address-columns">
        <!-- 左侧：当前地址 -->
        <div
          class="address-card"
          :class="{ 'card-selected': selectedOption === 'original' }"
          @click="selectedOption = 'original'"
        >
          <div class="card-header">
            <el-radio v-model="selectedOption" label="original" class="card-radio">{{ $t('ExternalOrders.newAdd.currentAddress') }}</el-radio>
          </div>
          <div class="card-section-title">{{ $t('ExternalOrders.newAdd.buyerAndReceivingInfo') }}</div>

          <!-- 逐行：展示 or 编辑 -->
          <div class="info-list" @click="isEditing && $event.stopPropagation()">
            <!-- Delivery Name -->
            <div class="info-row">
              <span class="info-label">{{ $t('ExternalOrders.newAdd.DeliveryName') }}:</span>
              <span v-if="!isEditing" class="info-value" :class="{ 'diff-tag': fieldDiff.receiverName }">{{ editForm.receiverName || '—' }}</span>
              <BjInput v-else v-model="editForm.receiverName" size="mini" :maxlength="MAX_LENGTH.DEFAULT" :placeholder="$t('inputText')" class="row-input" />
            </div>
            <!-- Delivery Phone -->
            <div class="info-row">
              <span class="info-label">{{ $t('ExternalOrders.newAdd.DeliveryPhone') }}:</span>
              <span v-if="!isEditing" class="info-value" :class="{ 'diff-tag': fieldDiff.phone }">{{ editForm.phone || '—' }}</span>
              <BjInput v-else v-model="editForm.phone" size="mini" :maxlength="MAX_LENGTH.DEFAULT" :placeholder="$t('inputText')" class="row-input" />
            </div>
            <!-- Country -->
            <div class="info-row">
              <span class="info-label">{{ $t('ExternalOrders.newAdd.DestinationCountry') }}<i v-if="fieldValidateStatus.country === false" class="el-icon-warning label-invalid-icon"></i>:</span>
              <span v-if="!isEditing" class="info-value" :class="{ 'diff-tag': fieldDiff.country }">{{ editForm.country || '—' }}</span>
              <BjSelectLov v-else v-model="editForm.country" size="mini" :option-list="COUNTRY_NAME_AND_TWOCODE" :placeholder="$t('chooseText')" class="row-input" />
            </div>
            <!-- State -->
            <div class="info-row">
              <span class="info-label">{{ $t('ExternalOrders.newAdd.DeliveryState') }}<i v-if="fieldValidateStatus.province === false" class="el-icon-warning label-invalid-icon"></i>:</span>
              <span v-if="!isEditing" class="info-value" :class="{ 'diff-tag': fieldDiff.province }">{{ editForm.province || '—' }}</span>
              <BjInput v-else v-model="editForm.province" size="mini" :maxlength="MAX_LENGTH.DEFAULT" :placeholder="$t('inputText')" class="row-input" />
            </div>
            <!-- Suburb -->
            <div class="info-row">
              <span class="info-label">{{ $t('ExternalOrders.newAdd.DeliverySuburb') }}<i v-if="fieldValidateStatus.district === false" class="el-icon-warning label-invalid-icon"></i>:</span>
              <span v-if="!isEditing" class="info-value" :class="{ 'diff-tag': fieldDiff.district }">{{ editForm.district || '—' }}</span>
              <BjInput v-else v-model="editForm.district" size="mini" :maxlength="MAX_LENGTH.DEFAULT" :placeholder="$t('inputText')" class="row-input" />
            </div>
            <!-- Postcode -->
            <div class="info-row">
              <span class="info-label">{{ $t('ExternalOrders.newAdd.DeliveryPostcode') }}<i v-if="fieldValidateStatus.zipCode === false" class="el-icon-warning label-invalid-icon"></i>:</span>
              <span v-if="!isEditing" class="info-value" :class="{ 'diff-tag': fieldDiff.zipCode }">{{ editForm.zipCode || '—' }}</span>
              <BjInput v-else v-model="editForm.zipCode" size="mini" :maxlength="MAX_LENGTH.SHORT_NAME" :placeholder="$t('inputText')" class="row-input" />
            </div>
            <!-- Address1 -->
            <div class="info-row">
              <span class="info-label">{{ $t('ExternalOrders.newAdd.DeliveryAddress1') }}<i v-if="fieldValidateStatus.deliveryAddress1 === false" class="el-icon-warning label-invalid-icon"></i>:</span>
              <span v-if="!isEditing" class="info-value" :class="{ 'diff-tag': fieldDiff.deliveryAddress1 }">{{ editForm.deliveryAddress1 || '—' }}</span>
              <BjInput v-else v-model="editForm.deliveryAddress1" size="mini" type="textarea" :autosize="{ minRows: 1, maxRows: 3 }" :maxlength="MAX_LENGTH.DEFAULT" :placeholder="$t('inputText')" class="row-input" />
            </div>
            <!-- Address2 -->
            <div class="info-row">
              <span class="info-label">{{ $t('ExternalOrders.newAdd.DeliveryAddress2') }}:</span>
              <span v-if="!isEditing" class="info-value" :class="{ 'diff-tag': fieldDiff.deliveryAddress2 }">{{ editForm.deliveryAddress2 || '—' }}</span>
              <BjInput v-else v-model="editForm.deliveryAddress2" size="mini" type="textarea" :autosize="{ minRows: 1, maxRows: 3 }" :maxlength="MAX_LENGTH.DEFAULT" :placeholder="$t('inputText')" class="row-input" />
            </div>
            <!-- Email -->
            <div class="info-row">
              <span class="info-label">{{ $t('ExternalOrders.newAdd.emailAddress') }}:</span>
              <span v-if="!isEditing" class="info-value" :class="{ 'diff-tag': fieldDiff.deliveryEmailAddress }">{{ editForm.deliveryEmailAddress || '—' }}</span>
              <BjInput v-else v-model="editForm.deliveryEmailAddress" size="mini" :maxlength="MAX_LENGTH.NORMAL_REMARK" :placeholder="$t('inputText')" class="row-input" />
            </div>
          </div>

          <!-- 编辑 / 完成 切换按钮：选中右侧时禁用 -->
          <el-tooltip
            :disabled="selectedOption === 'original'"
            :content="$t('ExternalOrders.newAdd.selectCurrentAddressTip')"
            placement="top"
          >
            <span class="edit-address-link">
              <el-button
                type="text"
                :disabled="selectedOption !== 'original'"
                @click.stop="isEditing = !isEditing"
              >
                <i :class="isEditing ? 'el-icon-check' : 'el-icon-edit'"></i>
                {{ isEditing ? $t('ExternalOrders.newAdd.doneEdit') : $t('ExternalOrders.newAdd.editAddress') }}
              </el-button>
            </span>
          </el-tooltip>
        </div>

        <!-- 右侧：建议地址（只展示） -->
        <div
          class="address-card"
          :class="{ 'card-selected': selectedOption === 'suggestion', 'card-disabled': !hasValidSuggestion }"
          @click="hasValidSuggestion && (selectedOption = 'suggestion')"
        >
          <div class="recommended-badge">{{ $t('ExternalOrders.newAdd.RECOMMENDED') }}</div>
          <div class="card-header">
            <el-radio v-model="selectedOption" label="suggestion" class="card-radio" :disabled="!hasValidSuggestion">{{ $t('ExternalOrders.newAdd.suggestion') }}</el-radio>
          </div>
          <div class="card-section-title">{{ $t('ExternalOrders.newAdd.verifiedInfo') }}</div>
          <div class="info-list">
            <div class="info-row">
              <span class="info-label">{{ $t('ExternalOrders.newAdd.DeliveryName') }}:</span>
              <span class="info-value" :class="{ 'diff-tag suggestion': fieldDiff.receiverName }">{{ suggestionInfo.receiverName || '—' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ $t('ExternalOrders.newAdd.DeliveryPhone') }}:</span>
              <span class="info-value" :class="{ 'diff-tag suggestion': fieldDiff.phone }">{{ suggestionInfo.phone || '—' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ $t('ExternalOrders.newAdd.DestinationCountry') }}:</span>
              <span class="info-value" :class="{ 'diff-tag suggestion': fieldDiff.country }">{{ suggestionInfo.country || '—' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ $t('ExternalOrders.newAdd.DeliveryState') }}:</span>
              <span class="info-value" :class="{ 'diff-tag suggestion': fieldDiff.province }">{{ suggestionInfo.province || '—' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ $t('ExternalOrders.newAdd.DeliverySuburb') }}:</span>
              <span class="info-value" :class="{ 'diff-tag suggestion': fieldDiff.district }">{{ suggestionInfo.district || '—' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ $t('ExternalOrders.newAdd.DeliveryPostcode') }}:</span>
              <span class="info-value" :class="{ 'diff-tag suggestion': fieldDiff.zipCode }">{{ suggestionInfo.zipCode || '—' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ $t('ExternalOrders.newAdd.DeliveryAddress1') }}:</span>
              <span class="info-value" :class="{ 'diff-tag suggestion': fieldDiff.deliveryAddress1 }">{{ suggestionInfo.deliveryAddress1 || '—' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ $t('ExternalOrders.newAdd.DeliveryAddress2') }}:</span>
              <span class="info-value" :class="{ 'diff-tag suggestion': fieldDiff.deliveryAddress2 }">{{ suggestionInfo.deliveryAddress2 || '—' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ $t('ExternalOrders.newAdd.emailAddress') }}:</span>
              <span class="info-value" :class="{ 'diff-tag suggestion': fieldDiff.deliveryEmailAddress }">{{ suggestionInfo.deliveryEmailAddress || '—' }}</span>
            </div>
          </div>
          <!-- <div v-if="hasAnomaly" class="standardized-note">
            <i class="el-icon-info"></i> STANDARDIZED TO AU POST FORMATTING
          </div> -->
        </div>
      </div>

      <!-- 底部按钮 -->
      <span slot="footer" class="dialog-footer">
        <el-button @click="handleCancel">{{ $t('cancelText') }}</el-button>
        <el-button :disabled="selectedOption === 'suggestion' || isEditing" @click="handleUseOriginal">{{ $t('ExternalOrders.newAdd.useOriginal') }}</el-button>
        <el-button type="primary" :disabled="selectedOption === 'original' || !hasValidSuggestion" @click="handleUseSuggestion">{{ $t('ExternalOrders.newAdd.useSuggestion') }} →</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { thirdApi } from '@/api';
import { COUNTRY_NAME_AND_TWOCODE } from '@/utils/statics';

export default {
  name: 'buyerInfoDialog',
  props: {
    receiverInfoVO: {
      type: Object,
      default: null
    },
    isShowEdit: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      COUNTRY_NAME_AND_TWOCODE,
      dialogVisible: false,
      isEditing: false,
      selectedOption: 'suggestion',
      editForm: {},
      /** 推荐地址信息（由地址验证API返回） */
      suggestionInfoVO: {},
      /** 是否有有效的推荐地址 */
      hasValidSuggestion: false,
      /** 各地址字段验证状态（false 表示验证不通过，需显示红色叹号） */
      fieldValidateStatus: {
        country: null,
        province: null,
        district: null,
        zipCode: null,
        deliveryAddress1: null
      },
      displayFields: [
        { key: 'receiverName', label: 'ExternalOrders.newAdd.DeliveryName' },
        { key: 'phone', label: 'ExternalOrders.newAdd.DeliveryPhone' },
        { key: 'country', label: 'ExternalOrders.newAdd.DestinationCountry' },
        { key: 'province', label: 'ExternalOrders.newAdd.DeliveryState' },
        { key: 'district', label: 'ExternalOrders.newAdd.DeliverySuburb' },
        { key: 'zipCode', label: 'ExternalOrders.newAdd.DeliveryPostcode' },
        { key: 'deliveryAddress1', label: 'ExternalOrders.newAdd.DeliveryAddress1' },
        { key: 'deliveryAddress2', label: 'ExternalOrders.newAdd.DeliveryAddress2' },
        { key: 'deliveryEmailAddress', label: 'ExternalOrders.newAdd.emailAddress' }
      ]
    };
  },
  computed: {
    /** 推荐地址：优先使用API返回的推荐地址，否则回退到原始地址 */
    suggestionInfo() {
      return this.suggestionInfoVO || {};
    },
    fieldDiff() {
      // 仅在接口返回全部验证通过（hasValidSuggestion=true）时才计算左右差异
      // 否则全部返回 false，取消红蓝差异对比
      if (!this.hasValidSuggestion) {
        return {
          receiverName: false, phone: false, country: false, province: false,
          district: false, zipCode: false, deliveryAddress1: false,
          deliveryAddress2: false, deliveryEmailAddress: false
        };
      }
      const s = this.suggestionInfo;
      const e = this.editForm;
      const isDiff = (a, b) => (a || '') !== (b || '');
      return {
        receiverName: isDiff(e.receiverName, s.receiverName),
        phone: isDiff(e.phone, s.phone),
        country: isDiff(e.country, s.country),
        province: isDiff(e.province, s.province),
        district: isDiff(e.district, s.district),
        zipCode: isDiff(e.zipCode, s.zipCode),
        deliveryAddress1: isDiff(e.deliveryAddress1, s.deliveryAddress1),
        deliveryAddress2: isDiff(e.deliveryAddress2, s.deliveryAddress2),
        deliveryEmailAddress: isDiff(e.deliveryEmailAddress, s.deliveryEmailAddress)
      };
    },
    hasAnomaly() {
      return Object.values(this.fieldDiff).some(Boolean);
    },
    /** 是否存在验证不通过的字段（显示红色叹号的情况） */
    hasInvalidFields() {
      return Object.values(this.fieldValidateStatus).some(v => v === false);
    }
  },
  watch: {
    selectedOption(val) {
      if (val === 'suggestion') {
        this.isEditing = false;
      }
    }
  },
  created() {},
  methods: {
    // 验证原地址 并 获取推荐地址
    checkAddress() {
      const info = this.receiverInfoVO;
      if (!info) return;
      
      const POST_DATA = {
        country: info.country,
        city: info.district,
        state: info.province,
        postcode: info.zipCode,
        addressLines: [ info.deliveryAddress2?info.deliveryAddress1 + ',' + info.deliveryAddress2 : info.deliveryAddress1 ]
      };

      thirdApi.orderAddressVerification(POST_DATA).then(res => {
          if (res.failed == true) {
            // 接口返回失败，无有效推荐地址
            this.suggestionInfoVO = null;
            this.hasValidSuggestion = false;
            this.selectedOption = 'original';
            return;
          }
          // 检查是否有任意 ValidateStatus 为 false
          const hasInvalidField = [
            res.countryValidateStatus,
            res.cityValidateStatus,
            res.stateValidateStatus,
            res.postcodeValidateStatus,
            res.addressLineValidateStatus
          ].some(status => status === false);

          if (hasInvalidField) {
            // 存在验证不通过的字段：禁用右侧，置空 suggestionInfoVO（取消差异对比），记录各字段状态
            this.fieldValidateStatus = {
              country: res.countryValidateStatus,
              province: res.stateValidateStatus,
              district: res.cityValidateStatus,
              zipCode: res.postcodeValidateStatus,
              deliveryAddress1: res.addressLineValidateStatus
            };
            this.suggestionInfoVO = null;
            this.hasValidSuggestion = false;
            this.selectedOption = 'original';
            return;
          }

          // 所有字段验证通过：构建推荐地址，保留收件人基础信息，使用API返回的标准化地址字段
          this.suggestionInfoVO = {
            receiverName: info.receiverName,
            phone: info.phone,
            deliveryEmailAddress: info.deliveryEmailAddress,
            
            country: res.country,
            district: res.city,
            province: res.state,
            zipCode: res.postcode,
            deliveryAddress1: res.addressLines?.[0],
            deliveryAddress2: res.addressLines?.[1] || null,
          };
          this.hasValidSuggestion = true;
        })
        .catch(() => {
          this.suggestionInfoVO = {};
          this.hasValidSuggestion = false;
          this.selectedOption = 'original';
        })
    },
    openDialog() {
      this.editForm = Object.assign({}, this.receiverInfoVO || {});
      this.isEditing = false;
      this.suggestionInfoVO = {};
      this.hasValidSuggestion = false;
      this.fieldValidateStatus = { country: null, province: null, district: null, zipCode: null, deliveryAddress1: null };
      this.selectedOption = 'suggestion';
      this.dialogVisible = true;
      // 打开弹窗时才调用地址验证
      this.checkAddress();
    },
    handleCancel() {
      this.dialogVisible = false;
    },
    handleUseOriginal() {
      this.$emit('saveReceiverInfo', Object.assign({}, this.editForm), () => {
        this.dialogVisible = false; // ← closeDialog 就是这个箭头函数
      });
    },
    handleUseSuggestion() {
      this.$emit('saveReceiverInfo', Object.assign({}, this.suggestionInfo), () => {
        this.dialogVisible = false; // ← closeDialog 就是这个箭头函数
      });
    }
  }
};
</script>

<style scoped>
.anomaly-alert {
  background: #fff0f0;
  border: 1px solid #fbc4c4;
  border-radius: 4px;
  padding: 10px 16px;
  margin-bottom: 16px;
  color: #333333;
  font-size: 13px;
}
.anomaly-maps-link {
  color: #409EFF;
  text-decoration: underline;
  cursor: pointer;
  margin: 0 3px;
}
.address-columns {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}
.address-card {
  flex: 1;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px 16px 40px 16px;
  position: relative;
  transition: border-color 0.2s;
  background: #fff;
  cursor: pointer;
}
.card-selected {
  border-color: #409EFF;
}
.card-disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: #f5f5f5;
}
.recommended-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  background: #e8f4fd;
  color: #409EFF;
  font-size: 11px;
  font-weight: bold;
  padding: 2px 8px;
  border-radius: 3px;
  letter-spacing: 0.5px;
}
.card-header {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}
.card-radio {
  margin-right: 6px;
}
.card-section-title {
  font-size: 12px;
  font-weight: 700;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #eee;
  padding-bottom: 8px;
  margin-bottom: 10px;
}
.info-list {
  font-size: 13px;
}
.info-row {
  display: flex;
  align-items: center;
  padding: 4px 0;
  min-height: 28px;
  position: relative;
}
.info-label {
  color: #888;
  min-width: 140px;
  flex-shrink: 0;
}
.label-invalid-icon {
  color: #f56c6c;
  font-size: 12px;
  margin: 0 3px;
  vertical-align: middle;
}
.info-value {
  color: #333;
  font-weight: 500;
  word-break: break-word;
}
.row-input {
  flex: 1;
}
.diff-tag {
  background: #ffe0e0;
  color: #d9534f;
  padding: 1px 6px;
  border-radius: 3px;
  font-weight: bold;
}
.diff-tag.suggestion {
  background: #e8f0fe;
  color: #4285f4;
}
.edit-address-link {
  position: absolute;
  bottom: 12px;
  right: 16px;
  color: #409EFF;
  font-size: 13px;
  cursor: pointer;
  user-select: none;
}
.edit-address-link:hover {
  text-decoration: underline;
}
.standardized-note {
  margin-top: 16px;
  font-size: 11px;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
</style>
